import * as React from 'react'
import { IPet } from '../interfaces/types.ts'

const STORAGE_KEY = 'petCardOrder'

/**
 * Reads saved pet ID order from localStorage, applies it to the given pets array,
 * and appends any new pets (not in saved order) at the end.
 * Returns the ordered pets and a reorder function that persists to localStorage.
 */
export const usePetOrder = (pets: IPet[]) => {
    const [orderedPets, setOrderedPets] = React.useState<IPet[]>(pets)

    // Stabilize the dependency: only re-run when pet IDs actually change
    const petIds = pets.map(p => p._id).join(',')

    React.useEffect(() => {
        if (!pets.length) {
            setOrderedPets([])
            return
        }

        const savedOrder: string[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')

        if (!savedOrder.length) {
            // No saved order — use alphabetical default
            const sorted = [...pets].sort((a, b) => a.petName > b.petName ? 1 : -1)
            setOrderedPets(sorted)
            return
        }

        // Build ordered list: pets in saved order first, then new pets appended at end
        const petMap = new Map(pets.map(p => [p._id, p]))
        const ordered: IPet[] = []

        // Add pets in saved order (skip any that no longer exist)
        for (const id of savedOrder) {
            const pet = petMap.get(id)
            if (pet) {
                ordered.push(pet)
                petMap.delete(id)
            }
        }

        // Append remaining (new) pets alphabetically
        const remaining = Array.from(petMap.values()).sort((a, b) => a.petName > b.petName ? 1 : -1)
        ordered.push(...remaining)

        setOrderedPets(ordered)
    }, [petIds]) // eslint-disable-line react-hooks/exhaustive-deps

    const saveOrder = (reordered: IPet[]) => {
        const ids = reordered.map(p => p._id)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
        setOrderedPets(reordered)
    }

    return { orderedPets, saveOrder }
}

/**
 * Moves an item in an array from one index to another.
 */
export const arrayMove = <T,>(array: T[], from: number, to: number): T[] => {
    const newArray = [...array]
    const [item] = newArray.splice(from, 1)
    newArray.splice(to, 0, item)
    return newArray
}
